// Lógica pura del 020 (Constantes vitales / Balance hídrico) que el papel MSP pide y que antes faltaba o se perdía en silencio:
//  - Presión arterial: sistólica y diastólica son 2 renglones distintos del papel; una sola de las dos SÍ se guarda (antes se descartaba
//    si faltaba la otra).
//  - Día de internación y día postquirúrgico (instructivo, bloque B): 2 extensiones en cada Observation del registro.
//  - Hora de la toma (renglón HORA del papel): fija el `effectiveDateTime` del registro.
//  - Curva térmica: el instructivo pide trazar el pulso y la temperatura de las tomas de los 7 días; en el PDF firmado va como 1 gráfica con escala conjunta
//    (canal `curvasCrecimiento`, ya generalizado) armadas con las tomas guardadas del paciente en la ventana de 7 días de enfermería.
//  - Balance hídrico de 24 h: el papel totaliza el DÍA (todos los turnos); el PDF imprime el total del día con lo ya guardado, no solo del registro.
// Sin React ni MedplumClient.
import type { DatosCurvasCrecimientoPdf, GraficaCurvaPdf, SerieCurvaPdf } from '@logiacare/hce-pdf';
import type { Extension, Observation } from '@medplum/fhirtypes';
import { calcularBalanceHidrico, diaDeObservacion, fechaDeCelda, registrosLiquidosDe, turnoDeObservacion, type TotalesLiquidos, type Turno } from './enfermeria';
import { SIGNOS_VITALES } from './vitalSigns';

// ---------- Presión arterial ----------

/** "120 / 80", "120 / —" o "— / 80" (una sola de las dos también es dato). Vacío si no hay ninguna. */
export function textoPresionArterial(sistolica: number | '', diastolica: number | ''): string {
  if (sistolica === '' && diastolica === '') return '';
  return `${sistolica === '' ? '—' : sistolica} / ${diastolica === '' ? '—' : diastolica}`;
}

// ---------- Día de internación / postquirúrgico ----------

export const EXT_DIA_INTERNACION = 'http://logiacare.ec/fhir/StructureDefinition/dia-internacion';
export const EXT_DIA_POSTQUIRURGICO = 'http://logiacare.ec/fhir/StructureDefinition/dia-postquirurgico';

/** Un valor válido es un entero >= 0 (el postquirúrgico puede ser 0 el día de la cirugía); vacío u otro valor no genera extensión. */
export function extensionesDiasClinicos(diaInternacion: number | '', diaPostquirurgico: number | ''): Extension[] {
  const extensiones: Extension[] = [];
  if (diaInternacion !== '' && Number.isInteger(diaInternacion) && diaInternacion >= 0) extensiones.push({ url: EXT_DIA_INTERNACION, valueInteger: diaInternacion });
  if (diaPostquirurgico !== '' && Number.isInteger(diaPostquirurgico) && diaPostquirurgico >= 0) extensiones.push({ url: EXT_DIA_POSTQUIRURGICO, valueInteger: diaPostquirurgico });
  return extensiones;
}

// ---------- Hora de la toma ----------

const HORA_TOMA = /^([01]?\d|2[0-3]):([0-5]\d)$/;

export function horaTomaValida(texto: string): boolean {
  return HORA_TOMA.test(texto.trim());
}

/**
 * `effectiveDateTime` del registro: si hay hora de toma válida, esa hora del día de enfermería (las horas 0-7 caen en el día calendario
 * siguiente, ver `fechaDeCelda`); sin hora, el instante actual. `undefined` si la hora tecleada no es válida (el llamador avisa).
 */
export function efectivoDeToma(dia: string, hora: string, ahora: Date): string | undefined {
  const texto = hora.trim();
  if (texto === '') return ahora.toISOString();
  const m = HORA_TOMA.exec(texto);
  if (!m) return undefined;
  return fechaDeCelda(dia, Number(m[1]), Number(m[2])).toISOString();
}

// ---------- Ventana de 7 días de enfermería ----------

export const DIAS_HOJA_020 = 7;

/** `YYYY-MM-DD` + n días (aritmética en UTC: sin corrimientos por zona horaria ni horario de verano). */
export function sumarDias(dia: string, n: number): string {
  const [y, m, d] = dia.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d! + n)).toISOString().slice(0, 10);
}

function aUtc(dia: string): number {
  const [y, m, d] = dia.split('-').map(Number);
  return Date.UTC(y!, m! - 1, d!);
}

/** Primer día de enfermería de la hoja de 7 días que termina en `dia`. */
export function primerDiaHoja(dia: string): string {
  return sumarDias(dia, -(DIAS_HOJA_020 - 1));
}

// ---------- Curva térmica ----------

const LOINC_TEMPERATURA = SIGNOS_VITALES.find((s) => s.clave === 'temperatura')!.loinc;
const LOINC_PULSO = SIGNOS_VITALES.find((s) => s.clave === 'fc')!.loinc;

/** Posición de cada toma dentro del día (el día ocupa [i, i+1) en el eje X): AM, PM y HS en el centro de cada tercio. */
const DESPLAZAMIENTO_TURNO: Readonly<Record<Turno, number>> = { AM: 1 / 6, PM: 3 / 6, HS: 5 / 6 };

export interface TextosCurvaTermica {
  readonly titulo: string;
  readonly ejeX: string;
  readonly ejeYTemperatura: string;
  readonly ejeYPulso: string;
  /** Recibe el rango de fechas "aaaa-mm-dd a aaaa-mm-dd". */
  readonly nota: (rango: string) => string;
  readonly etiquetaTemperatura: string;
  readonly etiquetaPulso: string;
  readonly etiquetaNormal: string;
}

function puntosDe(observaciones: readonly Observation[], loinc: string, primerDia: string): { x: number; y: number }[] {
  const porToma = new Map<number, { x: number; y: number; cuando: string }>();
  for (const o of observaciones) {
    if (o.status === 'entered-in-error') continue;
    if (!o.code?.coding?.some((c) => c.system === 'http://loinc.org' && c.code === loinc)) continue;
    const valor = o.valueQuantity?.value;
    const dia = diaDeObservacion(o);
    const turno = turnoDeObservacion(o);
    if (typeof valor !== 'number' || !Number.isFinite(valor) || !dia || !turno) continue;
    const indiceDia = Math.round((aUtc(dia) - aUtc(primerDia)) / 86_400_000);
    if (indiceDia < 0 || indiceDia >= DIAS_HOJA_020) continue;
    const x = Math.round((indiceDia + DESPLAZAMIENTO_TURNO[turno]) * 1000) / 1000;
    const cuando = o.effectiveDateTime ?? '';
    const previo = porToma.get(x);
    // Dos tomas en el mismo turno y día: queda la más reciente.
    // Se compara el instante (no el texto): dos tomas con offsets distintos (`Z` vs `-05:00`) no se ordenan bien como cadenas.
    if (!previo || Date.parse(cuando) >= Date.parse(previo.cuando) || Number.isNaN(Date.parse(previo.cuando))) porToma.set(x, { x, y: valor, cuando });
  }
  return [...porToma.values()].map(({ x, y }) => ({ x, y }));
}

const PULSO_MIN = 40;
const PULSO_MAX = 140;
/** Correspondencia del papel entre las dos escalas: 37 °C = 70 lpm, 10 lpm = 1 °C. */
export const temperaturaEnLpm = (t: number): number => Math.round((10 * t - 300) * 1e6) / 1e6;
export const lpmEnTemperatura = (lpm: number): number => (lpm + 300) / 10;

/**
 * Curva térmica del 020 para el PDF firmado: UNA sola gráfica, como el papel (pulso y temperatura sobre la misma cuadrícula de 7 días de
 * enfermería que terminan en `dia`). La escala principal (izquierda, en rojo) es la del pulso (40-140 lpm) y la secundaria (a su lado, en azul)
 * la de la temperatura (34-44 °C, o más ancha si una toma se sale), con la misma correspondencia del papel: 37 °C cae en la fila de 70 lpm. `undefined` si no hay ni una toma
 * en la ventana. La temperatura lleva la línea discontinua de 37 °C que el papel imprime.
 */
export function datosCurvaTermica020Pdf(observaciones: readonly Observation[], dia: string, textos: TextosCurvaTermica): DatosCurvasCrecimientoPdf | undefined {
  const primer = primerDiaHoja(dia);
  const temperatura = puntosDe(observaciones, LOINC_TEMPERATURA, primer);
  const pulso = puntosDe(observaciones, LOINC_PULSO, primer);
  if (temperatura.length === 0 && pulso.length === 0) return undefined;
  const series: SerieCurvaPdf[] = [];
  if (temperatura.length > 0) {
    series.push({ etiqueta: textos.etiquetaNormal, estilo: 'referenciaDiscontinua', ejeSecundario: true, puntos: [{ x: 0, y: 37 }, { x: DIAS_HOJA_020, y: 37 }] });
    series.push({ etiqueta: textos.etiquetaTemperatura, estilo: 'paciente', color: 'azul', ejeSecundario: true, puntos: temperatura });
  }
  if (pulso.length > 0) series.push({ etiqueta: textos.etiquetaPulso, estilo: 'paciente', color: 'rojo', puntos: pulso });
  // Escala del papel: 10 lpm = 1 °C (37 °C cae en 70 lpm). Si alguna toma se sale de 40-140 lpm / 34-44 °C (p. ej. taquicardia de 160 o fiebre de 45),
  // los dos ejes se amplían juntos, de a 10, para que ningún punto se recorte en un PDF firmado.
  const equivalentes = [...pulso.map((p) => p.y), ...temperatura.map((p) => temperaturaEnLpm(p.y))];
  const yMin = Math.min(PULSO_MIN, Math.floor(Math.min(...equivalentes) / 10) * 10);
  const yMax = Math.max(PULSO_MAX, Math.ceil(Math.max(...equivalentes) / 10) * 10);
  const grafica: GraficaCurvaPdf = {
    titulo: textos.titulo,
    nota: textos.nota(`${primer} a ${dia}`),
    ejeXEtiqueta: textos.ejeX,
    ejeYEtiqueta: textos.ejeYPulso,
    xMin: 0,
    xMax: DIAS_HOJA_020,
    pasoX: 1,
    yMin,
    yMax,
    ejeYSecundario: { etiqueta: textos.ejeYTemperatura, yMin: lpmEnTemperatura(yMin), yMax: lpmEnTemperatura(yMax), paso: 1, color: 'azul' },
    series,
  };
  return { graficas: [grafica] };
}

// ---------- Balance de 24 h del día ----------

/** Totales de ingresos/eliminaciones/balance del día de enfermería `dia` (los 3 turnos), con las Observation de la sección D ya guardadas. `undefined` si ese día no tiene mL. */
export function balance24hDelDia(observacionesD: readonly Observation[], dia: string): TotalesLiquidos | undefined {
  const vigentes = observacionesD.filter((o) => o.status !== 'entered-in-error');
  return calcularBalanceHidrico(registrosLiquidosDe(vigentes)).find((b) => b.dia === dia)?.total24h;
}
